import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import { AuthProvider } from './lib/AuthContext'
import { LangProvider } from './lib/i18n'
import './styles/global.css'
import './styles/urself.css'
import './styles/app.css'
// Thème clair (9 octobre 2026) : la landing a sa propre feuille, et
// light.css re-teinte tout le reste (espace client, auth, onboarding,
// guides, légal) par-dessus urself.css + app.css.
import './styles/landing.css'
import './styles/light.css'
import './styles/register.css'
import './styles/dash.css'
import './styles/setup.css'
import './styles/welcome.css'
import './styles/ranks.css'
import './styles/mobile.css'
import './styles/rewards.css'
import './styles/rankpage.css'
import './styles/lockpage.css'
import './styles/modal.css'
import './styles/account.css'
import './styles/lp-mobile.css'
import { captureAttribution, initPixel } from './lib/tracking'
import { captureAffiliate } from './lib/affiliate'
import { PRERENDER } from './lib/prerender'
import { installDevMock } from './lib/devMock'
import { registerPwa } from './lib/pwa'

// Mock d'API pour travailler l'espace client sans backend (dev seulement,
// activé par localStorage.ctrl_mock = '1' — voir lib/devMock.js).
installDevMock()

// Le fbclid n'est présent que sur la toute première URL visitée : on le
// capte avant que React ne réécrive l'historique.
// Rien de tout cela dans le Chrome du prérendu : un robot n'est pas une
// visite, et un PageView à chaque `npm run build` fausserait le pixel.
if (!PRERENDER) {
  captureAttribution()
  // Lien d'affilié (?via=CODE) : gardé 60 jours, voir lib/affiliate.js.
  captureAffiliate()
  // Installable sur l'écran d'accueil (manifest + service worker en prod).
  registerPwa()
  // Le pixel Meta (fbevents.js, ~110 Ko, trois longues tâches) ne part plus
  // en même temps que la page : il attend que tout soit chargé et que le
  // processeur soit libre, OU le premier geste du visiteur — le premier des
  // deux. Rien n'est perdu : dès l'appel, `fbq` met en file ce qui arrive
  // avant que le script soit là. Ce qu'on gagne : le premier tap répond
  // sans attendre Facebook (INP, Core Web Vitals).
  let armed = false
  const arm = () => {
    if (armed) return
    armed = true
    for (const ev of ['pointerdown', 'keydown', 'touchstart', 'scroll']) window.removeEventListener(ev, arm)
    initPixel()
  }
  for (const ev of ['pointerdown', 'keydown', 'touchstart', 'scroll']) window.addEventListener(ev, arm, { passive: true, once: true })
  const idle = () => (window.requestIdleCallback ? window.requestIdleCallback(arm, { timeout: 2000 }) : setTimeout(arm, 1200))
  if (document.readyState === 'complete') idle()
  else window.addEventListener('load', idle, { once: true })
}

const container = document.getElementById('root')
const tree = (
  <React.StrictMode>
    <BrowserRouter>
      {/* La langue enveloppe l'authentification : les messages d'erreur
          remontés par l'API sont traduits, donc le contexte de langue doit
          exister avant que le premier appel réseau ne parte. */}
      <LangProvider>
        <AuthProvider>
          <App />
        </AuthProvider>
      </LangProvider>
    </BrowserRouter>
  </React.StrictMode>
)

// Les pages publiques arrivent PRÉRENDUES (build/<route>/index.html, voir
// scripts/prerender.js) : le conteneur contient déjà le HTML final. On
// l'HYDRATE — React rattache ses écouteurs au balisage existant — au lieu
// de tout jeter et redessiner, ce qui ferait clignoter la page.
//
// Si le balisage diffère (une page prérendue en largeur bureau ouverte sur
// un téléphone, où `useNarrow` change l'ordre des sections), React 18
// abandonne l'hydratation et redessine côté client : c'est le comportement
// documenté, il coûte un avertissement en console et rien de visible.
//
// Le tableau de bord n'est jamais prérendu (il dépend du compte) : son
// index.html reste vide et prend la voie classique.
if (container.hasChildNodes()) {
  ReactDOM.hydrateRoot(container, tree, {
    onRecoverableError: () => { /* mismatch attendu, cf. ci-dessus */ }
  })
} else {
  ReactDOM.createRoot(container).render(tree)
}
